<template>
  <div class="card__wrapper">
    <div class="card">
      <h1 class="card__title">{{ $t('messages.register') }}</h1>
      <div class="card__description">
        <b-form @submit.prevent="register">
          <b-form-group id="storeGroup">
            <b-form-checkbox id="store" name="store"
                             v-model="form.store"
                             class="checkboxNoMargin">&nbsp;</b-form-checkbox>
            <span class="checkboxLineHeight pointer" @click="click('store')">{{ $t('messages.register_store') }}</span>
            <b-button v-b-tooltip.hover :title="$t('messages.register_store_info')" class="ml-1 mr-1 noPadding noBackground"><font-awesome-icon icon="info-circle" class="red"></font-awesome-icon></b-button>
          </b-form-group>
          <b-form-group id="firstnameGroup"
                        v-bind:label="(!form.store ? $t('messages.firstname') : $t('messages.company_name')) +':'"
                        label-for="firstname"
                        :invalid-feedback="!form.store ? $t('errors.firstname') : $t('errors.company_name')">
            <b-form-input id="firstname" name="firstname" type="text"
                          v-model.trim="form.firstname"
                          @input="$v.form.firstname.$touch"
                          :state="$v.form.firstname.$dirty ? !$v.form.firstname.$error : null"></b-form-input>
          </b-form-group>
          <b-form-group id="lastnameGroup"
                        v-if="!form.store"
                        v-bind:label="$t('messages.lastname')+':'"
                        label-for="lastname"
                        :invalid-feedback="$t('errors.lastname')">
            <b-form-input id="lastname" name="lastname" type="text"
                          v-model.trim="form.lastname"
                          @input="$v.form.lastname.$touch"
                          :state="$v.form.lastname.$dirty ? !$v.form.lastname.$error : null"></b-form-input>
          </b-form-group>
          <b-form-group id="logoGroup"
                        v-if="form.store"
                        v-bind:label="$t('messages.logo')"
                        label-for="logo">
            <span class="small">{{ $t('messages.logo_info') }}</span>
            <b-form-file id="logo" name="logo" accept=".png"
                         v-model="logo"
                         :placeholder="$t('errors.logo')"
                         :browse-text="$t('messages.browse')"
                         @change="$v.logo.$touch"
                         :state="$v.logo.$dirty ? !$v.logo.$error : null"></b-form-file>
          </b-form-group>
          <b-form-group id="emailGroup"
                        v-bind:label="$t('messages.email')+':'"
                        label-for="email"
                        :invalid-feedback="emailError">
            <b-form-input id="email" name="email" type="text"
                          v-model.trim="form.email"
                          @input="$v.form.email.$touch"
                          :state="$v.form.email.$dirty ? !$v.form.email.$error : null"></b-form-input>
          </b-form-group>
          <b-form-group id="passwordGroup"
                        v-bind:label="$t('messages.password')+':'"
                        label-for="password"
                        :invalid-feedback="$t('errors.password')">
            <b-form-input id="password" name="password" type="password"
                          v-model.trim="form.password"
                          autocomplete="new-password"
                          @input="$v.form.password.$touch"
                          :state="$v.form.password.$dirty ? !$v.form.password.$error : null"></b-form-input>
          </b-form-group>
          <b-form-group id="repeatpasswordGroup"
                        v-bind:label="$t('messages.repeatpassword')+':'"
                        label-for="repeatpassword"
                        :invalid-feedback="$t('errors.repeatpassword')">
            <b-form-input id="repeatpassword" name="repeatpassword" type="password"
                          v-model.trim="form.repeatpassword"
                          @input="$v.form.repeatpassword.$touch"
                          :state="$v.form.repeatpassword.$dirty ? !$v.form.repeatpassword.$error : null"></b-form-input>
          </b-form-group>
          <b-form-group id="locationGroup"
                        v-bind:label="$t('messages.city')+':'"
                        label-for="location"
                        :invalid-feedback="$t('errors.city')">
            <span class="small"><strong>{{ $t('messages.city_explanation') }}</strong></span>
            <location-picker id="location" name="location" class="mt-1"
                             :setCityAndCountry="setCityAndCountry"
                             :isValidState="($v.form.city.$dirty || $v.form.country.$dirty) ? (!$v.form.city.$error && !$v.form.country.$error) : null"></location-picker>
          </b-form-group>
          <b-form-group id="disclaimerGroup">
            <b-form-checkbox id="disclaimer" name="disclaimer"
                             v-model="form.disclaimer"
                             @input="$v.form.disclaimer.$touch"
                             :state="$v.form.disclaimer.$dirty ? !$v.form.disclaimer.$error : null"
                             class="checkboxNoMargin">&nbsp;</b-form-checkbox>
            <span class="checkboxLineHeight pointer" @click="click('disclaimer')">{{ $t('messages.disclaimer_pre') }} <b-button variant="link" class="buttonNoPadding" v-b-modal.disclaimerModal>{{ $t('messages.disclaimer') }}</b-button> {{ $t('messages.disclaimer_post') }}</span>
          </b-form-group>
          <b-form-group id="dataGroup">
            <b-form-checkbox id="data" name="data"
                             v-model="form.data"
                             @input="$v.form.data.$touch"
                             :state="$v.form.data.$dirty ? !$v.form.data.$error : null"
                             class="checkboxNoMargin">&nbsp;</b-form-checkbox>
            <span class="checkboxLineHeight pointer" @click="click('data')">{{ $t('messages.data_pre') }} <b-button variant="link" class="buttonNoPadding" v-b-modal.dataModal>{{ $t('messages.data') }}</b-button> {{ $t('messages.data_post') }}</span>
          </b-form-group>
          <b-form-group id="registerGroup">
            <div class="d-flex-responsive align-items-center">
              <notification-button :message="$t('messages.register')" :notification="notifications.register" field="register"
                                   :disabled="$v.$invalid"></notification-button>
              <p class="font-italic ml-2 register-activate">{{ $t('messages.register_activate') }}</p>
            </div>
          </b-form-group>
        </b-form>
      </div>
    </div>

    <b-modal id="disclaimerModal" :title="$t('messages.disclaimer_title')" hide-footer>
      <p v-html="getDisclaimer()"></p>
    </b-modal>

    <b-modal id="dataModal" :title="$t('messages.data_title')" hide-footer>
      <p v-html="getData()"></p>
    </b-modal>
  </div>
</template>

<script>
import LocationPicker from '../components/LocationPicker'
import { validationMixin } from 'vuelidate'
import { required, requiredIf, email, minLength, sameAs } from 'vuelidate/lib/validators'
import NotificationButton from '../components/NotificationButton'

import { notificationMixin } from '../mixin/notification'
import { fileSize, fileExtension } from '../lib/validators'

import { userService } from '../services/user.service'

export default {
  name: 'register',
  metaInfo: function () {
    return {
      title: this.$i18n.t('messages.register') + ' - CrowdFinder',
      meta: [
        {
          'property': 'og:title',
          'content': this.$i18n.t('messages.register'),
          'vmid': 'og:title'
        }
      ]
    }
  },
  components: { LocationPicker, NotificationButton },
  data: function () {
    return {
      form: {},
      logo: null,
      notifications: {
        register: null
      }
    }
  },
  mixins: [ validationMixin, notificationMixin ],
  computed: {
    emailError: function () {
      if (!this.$v.form.email.isUnique) {
        return this.$i18n.t('errors.email_exists')
      }
      return this.$i18n.t('errors.email')
    }
  },
  validations: {
    form: {
      firstname: {
        required
      },
      lastname: {
        required: requiredIf(function (value) {
          return !this.form.store
        })
      },
      email: {
        required,
        email,
        isUnique (value) {
          let store = this.$store
          if (value === '') {
            return true
          }

          return new Promise((resolve, reject) => {
            userService.exists(value)
              .then((response) => {
                resolve(response)
              }, (error) => {
                store.commit('error', 'errors.email_exists_problem')
                reject(error)
              })
          })
        }
      },
      password: {
        required,
        minLength: minLength(6)
      },
      repeatpassword: {
        sameAs: sameAs('password')
      },
      city: {
        required
      },
      country: {
        required
      },
      lat: {
        required
      },
      lng: {
        required
      },
      disclaimer: {
        sameAs: sameAs(() => true)
      },
      data: {
        sameAs: sameAs(() => true)
      }
    },
    logo: {
      required: requiredIf(function (value) {
        return this.form.store
      }),
      fileSize: fileSize(100),
      fileExtension: fileExtension('png')
    }
  },
  methods: {
    click: function (id) {
      document.getElementById(id).click()
    },
    register: function () {
      if (!this.$v.$invalid) {
        let account = this.$v.form.$model
        let store = this.$store
        let router = this.$router

        account.language = this.$i18n.locale

        let formData = new FormData()
        formData.append('logo', this.logo)
        formData.append('account', JSON.stringify(account))

        userService.register(formData)
          .then(() => {
            router.push('/login')
            setTimeout(() => store.commit('success', 'success.register'))
          }, () => {
            store.commit('error', 'errors.register')
            this.sendNotification('register', 'error')
          })
      }
    },
    getDisclaimer: function () {
      let disclaimerUrl = 'i18n/disclaimer/' + this.$i18n.locale + '.html'
      return require('@/' + disclaimerUrl)
    },
    getData: function () {
      let dataUrl = 'i18n/privacy/' + this.$i18n.locale + '.html'
      return require('@/' + dataUrl)
    },
    setCityAndCountry: function (city, country, lat, lng) {
      this.$set(this.form, 'city', city)
      this.$set(this.form, 'country', country)
      this.$set(this.form, 'lat', lat)
      this.$set(this.form, 'lng', lng)
      this.$v.form.city.$touch()
      this.$v.form.country.$touch()
      this.$v.form.lat.$touch()
      this.$v.form.lng.$touch()
    }
  },
  created: function () {
    this.$root.$on('resetNotification', (notification) => {
      this.resetNotification(notification)
    })
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .buttonNoPadding {
    padding: 0px 0px 1.5px;
    font-size: inherit;
  }

  .register-activate {
    margin-top: 10px;
  }

  @media screen and (min-width: 901px) {
    .register-activate {
      margin-top: 0px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/Register.vue